import { getJwt } from './client';
import { SESSION_TOKEN_KEY } from './impersonation-handoff';

/**
 * Attaches the Better Auth JWT to same-origin `/api/*` requests — but ONLY for
 * a session that was PLANTED rather than signed into.
 *
 * A normal session does not need this. `server/routes/auth-proxy.ts` makes the
 * Better Auth cookie first-party on every origin (prod, each preview,
 * localhost), so a naked `fetch('/api/…')` already authenticates through
 * `verifyBetterAuthCookie` in `server/utils/auth.ts`. The app's chattiest path
 * — `clientRPC` — attaches the JWT explicitly anyway (`utils/hono-client.ts`),
 * which is what keeps the bulk of the traffic off that path's one `get-session`
 * subrequest per request. What is left over here is one-shot user actions
 * (uploads, TTS, AI streams): not worth authenticating the whole page's
 * `fetch` to save a hop none of them notice.
 *
 * A PLANTED token is a different matter — it is a DIFFERENT IDENTITY from
 * whatever cookie the browser holds. Admin impersonation and the e2e suite are
 * its only two writers (see `impersonation-handoff.ts`), and `getAuthenticatedUserId`
 * ranks Bearer above cookie precisely so one request cannot split across two
 * accounts. Without the wrapper, `/api/*` under impersonation would resolve the
 * ADMIN's own session, or nobody — and the work would land in the wrong account.
 *
 * Both writers land the token BEFORE this module evaluates: impersonation
 * hard-navigates to `/` after writing it (`components/impersonate-confirm.tsx`),
 * and e2e seeds it via `addInitScript`. So the check is a one-time read at
 * install — no planted token, no patch on `window.fetch` at all.
 *
 * `/api/auth/*` is excluded: the auth client owns those, and the JWT is fetched
 * THROUGH them, so the exclusion is also what breaks the recursion.
 */
let installed = false;

export function installAmbientAuthFetch(): void {
  if (installed || typeof window === 'undefined') return;
  // Deliberately before `installed` is set: a caller that runs this too early
  // (no token yet) must not latch a permanent no-op.
  if (!hasPlantedSessionToken()) return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    if (isAppApiRequest(input)) {
      const jwt = await getJwt();
      if (jwt) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined),
        );
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${jwt}`);
          init = { ...init, headers };
        }
      }
    }
    return original(input, init);
  };
}

/**
 * Storage can throw outright (private mode, blocked site data) rather than
 * answering null — and a token that cannot be read is a token that cannot be
 * carried, so both outcomes mean the same thing: nothing to plant.
 */
function hasPlantedSessionToken(): boolean {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY) !== null;
  } catch {
    return false;
  }
}

function isAppApiRequest(input: RequestInfo | URL): boolean {
  try {
    const raw =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(raw, window.location.origin);
    return (
      url.origin === window.location.origin &&
      url.pathname.startsWith('/api/') &&
      !url.pathname.startsWith('/api/auth')
    );
  } catch {
    return false;
  }
}
